// client/src/components/stats/nashPushFoldBenchmarks.ts
//
// Nash chipEV push ranges para push/fold short stack.
// Source: consenso de HoldemResources, SitNGo Wizard, ICMizer chipEV mode.
//
// Los valores aquí son FRECUENCIAS (% de manos shoveadas) no rangos concretos.
// Nash produce un rango exacto (las N% manos top) pero en GrindLog medimos
// frecuencia observada del user y la comparamos con la óptima.
//
// IMPORTANTE: estos números son para **chipEV, heads-up equity assumption**.
// En la realidad los rangos cambian con:
// - ICM (bubble / final table): tipicamente más TIGHT
// - Players behind hero: con 2+ detrás, más TIGHT
// - Meta del stake: a stakes bajos los villains pagan más loose → podés push más
//
// Por eso son benchmarks **aproximados** con un disclaimer visible en UI.
//
// Los valores actuales están weighted a ~10bb (el stack más común cuando hero
// está en push/fold territory). Para un user que juega principalmente 8bb,
// los rangos reales serían ligeramente más wide; para 14bb, más tight.

import { OpenPosition } from '../../utils/pokerStats/types';
import { Benchmark } from './benchmarks';

// ─────────────────────────────────────────────────────────────────────────────
// Push frequency benchmarks (weighted to ~10bb chipEV Nash)
// ─────────────────────────────────────────────────────────────────────────────
// These are the CORE push range benchmarks. User should push at least min%
// and at most max% of their RFI opportunities at <= 15bb stack.

export const NASH_PUSH_BENCHMARKS: Record<OpenPosition, Benchmark> = {
  // EP (6-max) / UTG (full ring) — tightest range
  // Nash at 10bb: ~12%, at 15bb: ~10%. Range allows for the user's
  // stack distribution.
  EP: { min: 9, max: 16, source: 'theoretical' },

  // HJ — slightly wider
  // Nash at 10bb: ~16%, at 15bb: ~13%.
  HJ: { min: 12, max: 20, source: 'theoretical' },

  // CO — significantly wider
  // Nash at 10bb: ~24%, at 15bb: ~18%.
  CO: { min: 18, max: 28, source: 'theoretical' },

  // BTN — very wide, only SB+BB to fold through
  // Nash at 10bb: ~40%, at 15bb: ~30%, at 8bb: ~47%.
  BTN: { min: 32, max: 48, source: 'theoretical' },

  // SB — widest, because you only need BB to fold
  // Nash at 10bb: ~55%, at 15bb: ~44%, at 8bb: ~62%.
  SB: { min: 44, max: 62, source: 'theoretical' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Leak frequencies — values that should be ZERO in pure theory
// ─────────────────────────────────────────────────────────────────────────────
// Any non-zero value here is a leak. We don't return a "healthy range" —
// we return a single threshold: "below this is tolerable, above is leak".

/** Any limp frequency above this (%) is flagged as a leak. */
export const LIMP_LEAK_THRESHOLD = 2;

/** Any min-raise frequency above this (%) is flagged as a leak. */
export const MINRAISE_LEAK_THRESHOLD = 5;

export function getPushFoldBenchmark(pos: OpenPosition): Benchmark | null {
  return NASH_PUSH_BENCHMARKS[pos] || null;
}
